/**
 * ShareCardLauncher — глобальный детектор `?share=<amount>` в URL.
 *
 * Сценарий: бот шлёт юзеру алерт об автосборе с inline-кнопкой
 * «📸 Поделиться в сторис», которая открывает мини-апп по
 * `https://luna.moonraid.online/?share=<amount>`. При первом
 * монтировании этого компонента мы:
 *   1. Парсим query-параметр `share`.
 *   2. Если он есть — показываем компактную модалку с одной кнопкой
 *      «Поделиться в сторис» (та же логика что в ClaimAnimation, без
 *      анимации клейма).
 *   3. Чистим параметр из URL (history.replaceState), чтобы при
 *      рефреше модалка не всплывала повторно.
 *
 * Модалка не блокирует юзера — есть «Закрыть».
 */
import { useEffect, useState } from 'react';
import { Share2, X } from 'lucide-react';
import { lunaApi } from '@/services/api';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';

type WebAppShare = {
  shareToStory?: (
    mediaUrl: string,
    params?: { text?: string; widget_link?: { url: string; name?: string } },
  ) => void;
};

export function ShareCardLauncher() {
  const { haptic } = useTelegramWebApp();
  const [amount, setAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const raw = sp.get('share');
    if (!raw) return;
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num <= 0) return;
    setAmount(num);
    sp.delete('share');
    const newSearch = sp.toString();
    const newUrl =
      window.location.pathname + (newSearch ? `?${newSearch}` : '');
    window.history.replaceState({}, '', newUrl);
  }, []);

  const close = () => {
    setAmount(null);
    setError('');
  };

  const handleShare = async () => {
    if (loading || amount === null) return;
    setError('');
    setLoading(true);
    haptic.light();
    try {
      const res = await lunaApi.shareCard.generate(amount);
      const url = res.data.url;
      const shareUrl =
        res.data.share_url ?? 'https://t.me/moonraid_game_bot';
      const tg = (window as unknown as { Telegram?: { WebApp?: WebAppShare } })
        .Telegram?.WebApp;
      if (tg?.shareToStory) {
        // Реф-ссылка в overlay-text (plain) + кликабельный sticker
        tg.shareToStory(url, {
          text: `LUNA PROTOKOL — пассивный доход на USDT 🚀\n${shareUrl}`,
          widget_link: { url: shareUrl, name: 'LUNA PROTOKOL' },
        });
        close();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
        close();
      }
    } catch (err) {
      const msg = (err as { message?: string }).message ?? 'Share failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (amount === null) return null;

  // СНГ формат: целое — без дробной (50), нецелое — один знак (15.5)
  const fmtAmount = (n: number) => {
    const r = Math.round(n * 10) / 10;
    const i = Math.trunc(r);
    return r === i ? `${i}` : `${i}.${Math.round(Math.abs(r - i) * 10)}`;
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-5"
      style={{
        background: 'rgba(0,0,0,0.78)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={close}
    >
      <div
        className="w-full max-w-sm rounded-3xl p-5"
        onClick={(e) => e.stopPropagation()}
        style={{
          // Плотный непрозрачный фон — чтобы дашборд за модалкой
          // не просвечивал и текст был читаемым.
          background: 'linear-gradient(180deg, #14141c, #0a0a10)',
          border: '1px solid var(--glass-stroke-strong)',
          boxShadow:
            '0 30px 80px -20px rgba(0,0,0,0.85), inset 0 1.5px 0 0 var(--glass-rim-light)',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <p className="font-display text-[12px] font-extrabold tracking-[0.18em] uppercase text-[var(--text-muted)]">
            Авто-сбор · {fmtAmount(amount)} USDT
          </p>
          <button
            type="button"
            onClick={close}
            data-no-sound="true"
            aria-label="Закрыть"
            className="text-[var(--text-muted)]"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[14px] text-[var(--text-primary)] mb-4 leading-snug">
          Цифры говорят сами за себя. Один тап — и результат
          у Вашей аудитории, с реф-кодом внутри.
        </p>
        <button
          type="button"
          onClick={handleShare}
          disabled={loading}
          data-no-sound="true"
          className="w-full rounded-[var(--radius-pill)] py-3.5 font-display text-[13px] font-extrabold tracking-[0.16em] uppercase text-[#0a0a0f] flex items-center justify-center gap-2 disabled:opacity-50"
          style={{
            background:
              'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 65%, var(--accent-secondary)))',
            boxShadow:
              '0 18px 36px -10px color-mix(in oklab, var(--accent-primary) 70%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.55)',
          }}
        >
          <Share2 size={15} strokeWidth={2.4} />
          Поделиться в сторис
        </button>
        {loading && (
          <p className="mt-1.5 text-center text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--accent-primary)]">
            Готовим
            <span className="luna-dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </p>
        )}
        {error && (
          <p className="mt-1.5 text-center text-[11px] text-[var(--status-negative)]">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
